/**
 * Main App - routing and auth wrapper
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import StudentDashboard from './pages/StudentDashboard';
import StudentFeedbackForm from './pages/StudentFeedbackForm';
import StudentFeedbackPreview from './pages/StudentFeedbackPreview';
import FacultyDashboard from './pages/FacultyDashboard';
import FacultyFeedbackPreview from './pages/FacultyFeedbackPreview';
import AdminDashboard from './pages/AdminDashboard';
import FeedbackQuestionnairePage from './pages/FeedbackQuestionnairePage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ParentDashboard from './pages/ParentDashboard';
import ParentFeedbackForm from './pages/ParentFeedbackForm';
import ParentFeedbackPreview from './pages/ParentFeedbackPreview';
import ParentStudentFeedbackPreview from './pages/ParentStudentFeedbackPreview';
import AlumniDashboard from './pages/AlumniDashboard';
import AlumniFeedbackForm from './pages/AlumniFeedbackForm';
import AlumniFeedbackPreview from './pages/AlumniFeedbackPreview';
import IndustryExpertDashboard from './pages/IndustryExpertDashboard';
import IndustryExpertFeedbackForm from './pages/IndustryExpertFeedbackForm';
import IndustryExpertFeedbackPreview from './pages/IndustryExpertFeedbackPreview';
import Unauthorized from './pages/Unauthorized';

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/student"
        element={
          <ProtectedRoute allowedRoles={['STUDENT']}>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/student/feedback"
        element={
          <ProtectedRoute allowedRoles={['STUDENT']}>
            <StudentFeedbackForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/student/preview/:submissionId"
        element={
          <ProtectedRoute allowedRoles={['STUDENT']}>
            <StudentFeedbackPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/faculty"
        element={
          <ProtectedRoute allowedRoles={['FACULTY']}>
            <FacultyDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/faculty/feedback"
        element={
          <ProtectedRoute allowedRoles={['FACULTY']}>
            <FacultyFeedbackPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin/questionnaire"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <FeedbackQuestionnairePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/super-admin"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/parent"
        element={
          <ProtectedRoute allowedRoles={['PARENT']}>
            <ParentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/parent/feedback"
        element={
          <ProtectedRoute allowedRoles={['PARENT']}>
            <ParentFeedbackForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/parent/preview/:submissionId"
        element={
          <ProtectedRoute allowedRoles={['PARENT']}>
            <ParentFeedbackPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/parent/student-preview/:submissionId"
        element={
          <ProtectedRoute allowedRoles={['PARENT']}>
            <ParentStudentFeedbackPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/alumni"
        element={
          <ProtectedRoute allowedRoles={['ALUMNI']}>
            <AlumniDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/alumni/feedback"
        element={
          <ProtectedRoute allowedRoles={['ALUMNI']}>
            <AlumniFeedbackForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/alumni/preview/:submissionId"
        element={
          <ProtectedRoute allowedRoles={['ALUMNI']}>
            <AlumniFeedbackPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/industry-expert"
        element={
          <ProtectedRoute allowedRoles={['INDUSTRY_EXPERT']}>
            <IndustryExpertDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/industry-expert/feedback"
        element={
          <ProtectedRoute allowedRoles={['INDUSTRY_EXPERT']}>
            <IndustryExpertFeedbackForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/industry-expert/preview/:submissionId"
        element={
          <ProtectedRoute allowedRoles={['INDUSTRY_EXPERT']}>
            <IndustryExpertFeedbackPreview />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const basename = process.env.PUBLIC_URL || '';
  return (
    <BrowserRouter basename={basename}>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
